import { Dispatcher, throwTraceError, Log } from '@fanfanlo';
import { ITriggerOrActionListPageParsedResult, ITriggerOrActionListParamsData } from './trigger-list-action-list-intf';
import { ProjectMemberType } from './projectMemberType';
import _ from 'lodash';
import { IAction } from '../entities/IAction';
import { ITrigger } from '../entities/ITrigger';

const fileLog = new Log(false, 'projectEventUtils_f')
fileLog.pause = true
fileLog.childrenPaused = true

const dispatcher = new Dispatcher()

enum EditType {
    add = "add",
    updateJavaData = "updateJavaData",
    updateWebConfig = "updateWebConfig",
}

export interface IProjectEditEvent<T> {
    editType?: EditType
    projectMemberType: ProjectMemberType
    classType?: string
    info?: ITriggerOrActionListPageParsedResult
    data: T
    projectInfo?: ITriggerOrActionListParamsData
}

function dispatchEditEvent<T>(editType: EditType, event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'dispatchEditEvent_fn')
    fnLog.pause = true
    fnLog.log('[dispatchEditEvent] editType=', editType, 'event=', JSON.stringify(event))
    dispatcher.dispatch(editType, event)
}

function onEditJavaData<T>(event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onEditJavaData_fn')
    fnLog.pause = true
    fnLog.log('[onEditJavaData] event=', JSON.stringify(event))

    switch (event.projectMemberType) {
        case ProjectMemberType.action:
            onActionEdit(EditType.updateJavaData, event);
            break;
        case ProjectMemberType.trigger:
            onTriggerEdit(EditType.updateJavaData, event);
            break;
        default: {
            const msg = `[onEditJavaData] unknown projectMemberType: ${event.projectMemberType}`
            fnLog.error(msg, event)
            throwTraceError(msg, event)
        }
    }
}

function onEditWebConfig<T>(event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onEditWebConfig_fn')
    fnLog.pause = true
    fnLog.log('[onEditWebConfig] event=', JSON.stringify(event))

    switch (event.projectMemberType) {
        case ProjectMemberType.action:
            onActionEdit(EditType.updateWebConfig, event);
            break;
        case ProjectMemberType.trigger:
            onTriggerEdit(EditType.updateWebConfig, event);
            break;
        default: {
            const msg = `[onEditWebConfig] unknown projectMemberType: ${event.projectMemberType}`
            fnLog.error(msg, event)
            throwTraceError(msg, event)
        }
    }
}

function onTriggerEdit<T>(editType: EditType, event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onTriggerEdit_fn')
    fnLog.pause = true
    fnLog.log('[onTriggerEdit] editType=', editType)

    const trigger = event.projectInfo?.trigger;
    if (!trigger) {
        const msg = `[onTriggerEdit] no trigger when editing trigger`
        fnLog.error(msg, event)
        return throwTraceError(msg, event)
    }
    fnLog.log('[onTriggerEdit] before merge, trigger.javaData=', JSON.stringify(trigger.javaData))
    fnLog.log('[onTriggerEdit] before merge, trigger.webConfig=', JSON.stringify(trigger.webConfig))

    switch (editType) {
        case EditType.updateJavaData:
            _.merge(trigger.javaData, event.data);
            fnLog.log('[onTriggerEdit] after merge javaData=', JSON.stringify(trigger.javaData))
            break;
        case EditType.updateWebConfig:
            _.merge(trigger.webConfig, event.data)
            fnLog.log('[onTriggerEdit] after merge webConfig=', JSON.stringify(trigger.webConfig))
            break;
        default: {
            const msg = `[onTriggerEdit] unknown editType: ${editType}`
            fnLog.error(msg, event)
            throwTraceError(msg, event)
        }
    }
}

function onActionEdit<T>(editType: EditType, event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onActionEdit_fn')
    fnLog.pause = true
    fnLog.log('[onActionEdit] editType=', editType)

    const action = event.projectInfo?.action;
    if (!action) {
        const msg = `[onActionEdit] no action when editing action`
        fnLog.error(msg, event)
        return throwTraceError(msg, event)
    }
    fnLog.log('[onActionEdit] before merge, action.javaData=', JSON.stringify(action.javaData))
    fnLog.log('[onActionEdit] before merge, action.webConfig=', JSON.stringify(action.webConfig))

    switch (editType) {
        case EditType.updateJavaData:
            _.merge(action.javaData, event.data);
            fnLog.log('[onActionEdit] after merge javaData=', JSON.stringify(action.javaData))
            break;
        case EditType.updateWebConfig:
            _.merge(action.webConfig, event.data)
            fnLog.log('[onActionEdit] after merge webConfig=', JSON.stringify(action.webConfig))
            break;
        default: {
            const msg = `[onActionEdit] unknown editType: ${editType}`
            fnLog.error(msg, event)
            throwTraceError(msg, event)
        }
    }
}

function onAdd<T>(event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onAdd_fn')
    fnLog.pause = true
    fnLog.log('[onAdd] event=', JSON.stringify(event))

    switch (event.projectMemberType) {
        case ProjectMemberType.actionGroup:
            onActionGroupAdd(event);
            break;
        case ProjectMemberType.triggerGroup:
            onTriggerGroupAdd(event);
            break;
        default: {
            const msg = `[onAdd] unknown projectMemberType: ${event.projectMemberType}`
            fnLog.error(msg, event)
        }
    }
}

function onTriggerGroupAdd<T>(event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onTriggerGroupAdd_fn')
    fnLog.pause = true

    const group = event.projectInfo?.triggerGroup
    if (!group) {
        const msg = `[onTriggerGroupAdd] no triggerGroup when adding trigger`
        fnLog.error(msg, event)
        return throwTraceError(msg, event)
    }

    const trigger = event.data as ITrigger

    // v2数据结构：children存储完整对象，list由后端维护
    if (!group.children) {
        group.children = []
        fnLog.log('[onTriggerGroupAdd] initialized empty children for triggerGroup')
    }

    fnLog.log('[onTriggerGroupAdd] before push, children.length=', group.children.length)

    // 修改：只维护children（存储完整对象），list由后端维护
    group.children.push(trigger)

    fnLog.log('[onTriggerGroupAdd] after push, children.length=', group.children.length)
    fnLog.log('[onTriggerGroupAdd] new trigger ulid=', trigger.ulid, 'trigger=', JSON.stringify(trigger))
}

function onActionGroupAdd<T>(event: IProjectEditEvent<T>) {
    const fnLog = fileLog.sub(false, 'onActionGroupAdd_fn')
    fnLog.pause = true

    const group = event.projectInfo?.actionGroup
    if (!group) {
        const msg = `[onActionGroupAdd] no actionGroup when adding action`
        fnLog.error(msg, event)
        return throwTraceError(msg, event)
    }

    const action = event.data as IAction

    // v2数据结构：children存储完整对象，list由后端维护
    if (!group.children) {
        group.children = []
        fnLog.log('[onActionGroupAdd] initialized empty children for actionGroup')
    }

    fnLog.log('[onActionGroupAdd] before push, children.length=', group.children.length)

    // 修改：只维护children（存储完整对象），list由后端维护
    group.children.push(action)

    fnLog.log('[onActionGroupAdd] after push, children.length=', group.children.length)
    fnLog.log('[onActionGroupAdd] new action ulid=', action.ulid, 'action=', JSON.stringify(action))
}

function init() {
    const fnLog = fileLog.sub(false, 'init_fn')
    fnLog.pause = true
    fnLog.log('[init] registering event listeners')
    dispatcher.addListener(EditType.updateJavaData, onEditJavaData)
    dispatcher.addListener(EditType.updateWebConfig, onEditWebConfig)
    dispatcher.addListener(EditType.add, onAdd)
    fnLog.log('[init] event listeners registered')
}

export const projectEventUtils = {
    init,
    dispatcher,
    dispatchEditEvent,
    EditType,
}
